import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { registerCompany } from "../../redux/features/authSlice";
import { 
  getBusinessConfig
} from "../../utils/businessTypeUtils";
import { ArrowRight, Eye, EyeOff, Shield, Zap, Database, Building, FileText, FileSpreadsheet, Layers, Users, Package, Car, Megaphone, BarChart3 } from "lucide-react";
import { usePageTracking } from "../../hooks/usePageTracking";
import { cacheVisitorEmail } from "../../utils/visitorEmail";
import { API_BASE_URL } from "../../config/api.js";
import axios from "axios";
import {
  trackCtaClick,
  trackFormSubmit,
  trackFormSuccess,
  trackFormError
} from "../../utils/googleAnalytics";
import EmailVerificationModal from "../../components/auth/EmailVerificationModal";

const REGISTER_PACKAGES = [2, 3, 4, 5];
const PACKAGE_BY_NAME = {
  starter: 2,
  standard: 3,
  business: 4,
  biznis: 4,
  premium: 5
};

function parseRegisterPackage(searchParams) {
  const raw = (searchParams.get("package") || searchParams.get("plan") || "").trim();
  if (!raw) return 2;
  const named = PACKAGE_BY_NAME[raw.toLowerCase()];
  if (named) return named;
  const n = Number(raw);
  return REGISTER_PACKAGES.includes(n) ? n : 2;
}

const RegisterCompanyNew = () => {
  const [countryCode] = useState("rs");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [registeredCompanyName, setRegisteredCompanyName] = useState('');

  // Track page visit
  usePageTracking('register');
  const [businessConfig, setBusinessConfig] = useState({
    needsServiceAddress: true,
    needsGarageAddress: false,
    needsSpecializations: false,
    needsServiceableApplianceTypes: false,
    needsServiceRadius: false,
    needsWarranty: true,
    needsInventory: false,
    needsMaintenanceContracts: false
  });
  const [formData, setFormData] = useState(() => ({
    companyName: "",
    ownerName: "",
    email: "",
    address: "",
    city: "",
    country: "rs",
    password: "",
    confirmPassword: "",
    phone: "",
    businessType: "Home Appliance Technician",
    package: parseRegisterPackage(new URLSearchParams(window.location.search)),
    garageAddress: "",
    specializations: [],
    serviceableApplianceTypes: [],
    serviceRadius: "",
    defaultWarrantyDuration: "",
    hasInventory: false,
    offersMaintenanceContracts: false,
    promoCode: "",
    marketingConsent: false
  }));

  useEffect(() => {
    const pkg = parseRegisterPackage(searchParams);
    setFormData((prev) => (prev.package === pkg ? prev : { ...prev, package: pkg }));
  }, [searchParams]);

  useEffect(() => {
    const email = String(formData.email || '').trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return undefined;

    const timer = setTimeout(() => {
      axios
        .post(`${API_BASE_URL}/api/analytics/track/registration-email`, {
          email,
          companyName: formData.companyName,
          package: formData.package,
          trackingId: sessionStorage.getItem('analytics_tracking_id'),
          pagePath: window.location.pathname + window.location.search
        })
        .catch(() => {});
    }, 700);

    return () => clearTimeout(timer);
  }, [formData.email, formData.companyName, formData.package]);

  useEffect(() => {
    const config = getBusinessConfig(formData.businessType);
    setBusinessConfig(config);
  }, [formData.businessType]);

  const validateField = (name, value) => {
    let tempErrors = { ...errors };
    
    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      tempErrors.email = emailRegex.test(value) ? "" : "Unesite validnu email adresu";
    }
    
    if (name === "password") {
      tempErrors.password = value.length >= 6 ? "" : "Lozinka mora imati najmanje 6 karaktera";
    }
    
    if (name === "confirmPassword") {
      tempErrors.confirmPassword = value === formData.password ? "" : "Lozinke se ne poklapaju";
    }
    
    setErrors(tempErrors);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = name === "package" ? Number(value) : type === "checkbox" ? checked : value;
    setFormData({ ...formData, [name]: nextValue });
    if (name === "email") cacheVisitorEmail(value);
    validateField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting) {
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Lozinke se ne poklapaju");
      return;
    }

    if (Object.values(errors).some(error => error !== "")) {
      toast.error("Molimo popravite greške pre slanja");
      return;
    }

    setIsSubmitting(true);
    trackFormSubmit('register', {
      package: formData.package,
      business_type: formData.businessType
    });
    try {
      const result = await dispatch(registerCompany({
        formData: {
          ...formData,
          countryCode: formData.country || countryCode,
          package: Number(formData.package)
        },
        navigate
      })).unwrap();
      
      trackFormSuccess('register', {
        package: formData.package,
        business_type: formData.businessType,
        requires_verification: !!(result?.requiresVerification || result?.emailSent !== false)
      });
      
      // Check if email verification is required
      if (result?.requiresVerification || result?.emailSent !== false) {
        setRegisteredEmail(result?.email || result?.result?.email || formData.email);
        setRegisteredCompanyName(result?.companyName || result?.result?.companyName || formData.companyName);
        setShowVerificationModal(true);
      }
    } catch (error) {
      console.error("Registration error:", error);
      trackFormError('register', {
        package: formData.package,
        message: error?.message || 'unknown'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogin = () => {
    trackCtaClick('login', { cta_location: 'register_page' });
    navigate("/auth?role=company&type=login");
  };

  return (
    <>
      {showVerificationModal && (
        <EmailVerificationModal
          email={registeredEmail}
          companyName={registeredCompanyName}
          onClose={() => setShowVerificationModal(false)}
          onResend={() => {
            // Modal will handle resend
          }}
        />
      )}
      
      {!showVerificationModal && (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-900 flex">
      {/* Left Side - Register Form */}
      <div className="flex-1 flex items-start justify-start px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 lg:pt-10">
        <div className="max-w-2xl w-full space-y-4">
          {/* Header */}
          <div className="mb-2 text-center">
            <div className="flex items-center justify-center gap-3">
              <div className="bg-green-600 p-3 rounded-full flex-shrink-0">
                <Building className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white">
                Kreirajte svoj nalog
              </h2>
            </div>
            <p className="text-gray-300 mt-2">
              Organizujte svoje poslovanje sa Spintaskerom po svojoj meri
            </p>
          </div>

          {/* Register Form */}
          <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
            {/* Row 1: Company Name and Full Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Company Name */}
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-300 mb-1">
                  Ime kompanije
                </label>
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  required
                  value={formData.companyName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                  placeholder="Unesite ime kompanije"
                />
              </div>

              {/* Owner Name */}
              <div>
                <label htmlFor="ownerName" className="block text-sm font-medium text-gray-300 mb-1">
                  Vaše ime i prezime
                </label>
                <input
                  id="ownerName"
                  name="ownerName"
                  type="text"
                  required
                  value={formData.ownerName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                  placeholder="Unesite vaše ime i prezime"
                />
              </div>
            </div>

            {/* Row 2: Email and Business Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${
                    errors.email ? 'border-red-500' : 'border-gray-600'
                  }`}
                  placeholder="Unesite email"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                )}
              </div>

              {/* Business Type */}
              <div>
                <label htmlFor="businessType" className="block text-sm font-medium text-gray-300 mb-1">
                  Tip biznisa
                </label>
                <select
                  id="businessType"
                  name="businessType"
                  required
                  value={formData.businessType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                >
                  <option value="Home Appliance Technician">Servis kućnih aparata</option>
                  <option value="IT Technician">TV i servis računara</option>
                </select>
              </div>
            </div>

            {/* Row 3: Address and City */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Address */}
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-300 mb-1">
                  Adresa
                </label>
                <input
                  id="address"
                  name="address"
                  type="text"
                  required
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                  placeholder="Unesite adresu"
                />
              </div>

              {/* City */}
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-300 mb-1">
                  Grad
                </label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  required
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                  placeholder="Unesite grad"
                />
              </div>
            </div>

            {/* Row 4: Country and Package */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Country */}
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-300 mb-1">
                  Država
                </label>
                <select
                  id="country"
                  name="country"
                  required
                  value={formData.country}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                >
                  <option value="rs">Srbija</option>
                  <option value="ba">Bosna i Hercegovina</option>
                  <option value="hr">Hrvatska</option>
                  <option value="me">Crna Gora</option>
                  <option value="mk">Severna Makedonija</option>
                  <option value="si">Slovenija</option>
                  <option value="hu">Mađarska</option>
                  <option value="ro">Rumunija</option>
                  <option value="bg">Bugarska</option>
                </select>
              </div>

              {/* Package Selection */}
              <div>
                <label htmlFor="package" className="block text-sm font-medium text-gray-300 mb-1">
                  Izaberite paket
                </label>
                <select
                  id="package"
                  name="package"
                  required
                  value={formData.package}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                >
                  <option value={2}>Starter</option>
                  <option value={3}>Standard</option>
                  <option value={4}>Biznis</option>
                  <option value={5}>Premium</option>
                </select>
                <p className="mt-2 text-sm text-green-400">
                  Mesec dana probni period uz svaki paket.
                </p>
              </div>
            </div>

            {/* Row 5: Password and Confirm Password */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                  Lozinka
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors pr-12 ${
                      errors.password ? 'border-red-500' : 'border-gray-600'
                    }`}
                    placeholder="Kreirajte lozinku"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-400">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1">
                  Potvrda lozinke
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors pr-12 ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-600'
                    }`}
                    placeholder="Potvrdite lozinku"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            {/* Marketing consent */}
            <div className="mt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="marketingConsent"
                  checked={formData.marketingConsent}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-800 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm text-gray-300">
                  Želim da primam obaveštenja o akcijama, novostima i ponudama SpinTasker-a na email.
                </span>
              </label>
            </div>

            {/* Promo Code Input */}
            <div className="mt-2">
              <label htmlFor="promoCode" className="block text-sm font-medium text-gray-300 mb-1">
                Promo kod (opciono)
              </label>
              <input
                type="text"
                id="promoCode"
                name="promoCode"
                value={formData.promoCode || ''}
                onChange={handleChange}
                placeholder="npr. FIRMA0001"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">
                Ako unesete promo kod postojećeg korisnika, on dobija mesec dana gratis pretplate.
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white transition-colors ${
                isSubmitting 
                  ? 'bg-gray-600 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
              }`}
            >
              <div className="flex items-center">
                {isSubmitting ? 'Kreiranje naloga...' : 'Kreiraj nalog'}
                {!isSubmitting && <ArrowRight className="ml-2 h-4 w-4" />}
              </div>
            </button>

            {/* Login Link */}
            <div className="text-center mt-4">
              <p className="text-sm text-gray-300">
                Već imate nalog?{" "}
                <button
                  type="button"
                  onClick={handleLogin}
                  className="text-green-400 hover:text-green-300 font-medium"
                >
                  Prijavite se
                </button>
              </p>
            </div>

          </form>
        </div>
      </div>

      {/* Right Side - Features */}
      <div className="hidden lg:flex lg:flex-1 bg-green-600 p-12 flex-col justify-center text-white">
        <div className="max-w-md">
          <h3 className="text-3xl font-bold mb-6">
            Sve što vam treba za uspešan servis na jednom mestu
          </h3>
          
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-4">
                <FileText className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Bez papira i Excel tabela</h4>
                <p className="text-white text-sm">
                  Zaboravite na gomile papira i komplikovane Excel tabele. Sve je digitalno i na jednom mestu.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-4">
                <Layers className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Sve centralizovano</h4>
                <p className="text-white text-sm">
                  Nalozi, klijenti, roba, vozila, marketing i analitika - sve na jednom mestu. 
                  Više nema potrage kroz različite fajlove i aplikacije.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-4">
                <Zap className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Praćenje u realnom vremenu</h4>
                <p className="text-white text-sm">
                  Vidite gde su vaši serviseri, koji poslovi su u toku i šta se dešava u vašem biznisu - sve u realnom vremenu.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-4">
                <Users className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Upravljanje klijentima i poslovima</h4>
                <p className="text-white text-sm">
                  Kompletna baza klijenata, istorija poslova, servisni nalozi i sve informacije o svakom klijentu na jednom mestu.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-4">
                <Package className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Upravljanje robom i inventarom</h4>
                <p className="text-white text-sm">
                  Pratite zalihu delova, rezervne delove, magacin i sve što vam treba za servis - sve na jednom mestu.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-4">
                <Car className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Upravljanje vozilima</h4>
                <p className="text-white text-sm">
                  Pratite vaša službena vozila, troškove, održavanje i sve vezano za flotu - sve na jednom mestu.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-4">
                <Megaphone className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Marketing i komunikacija</h4>
                <p className="text-white text-sm">
                  SMS, Viber, Email i WhatsApp poruke, loyalty programi i marketing strategije - sve integrisano.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-4">
                <BarChart3 className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Analitika i izveštaji</h4>
                <p className="text-white text-sm">
                  Vidite koliko zaradujete, koji su najprofitabilniji poslovi, analitika radnika i sve što vam treba za donošenje odluka.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
      )}
    </>
  );
};

export default RegisterCompanyNew;
